import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BedDouble, MapPin } from "lucide-react";
import { lpApi, lpErr, when, card, h2, input, btn, ghost } from "@/components/portal/lp/lpApi";

/** PACT Life Pass check-out / back-in, the same flow as the phone app. */
export function LifePassCard({ token }) {
  const api = lpApi(token);
  const [s, setS] = useState(null);
  const [f, setF] = useState({ destination: "", expected_return: "" });
  const load = useCallback(() => api.get("/portal/resident/campus-status").then(setS).catch(() => setS({ error: true })), [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (!s) return <section className={`${card} h-[120px] animate-pulse`} data-testid="home-lifepass-loading" />;
  if (s.error) return <section className={`${card} text-sm text-red-300`} data-testid="home-lifepass-error">Couldn't load your PACT Life Pass. <button type="button" className="underline" onClick={load}>Try again</button></section>;
  const out = s.status === "off_campus";
  const co = s.active_checkout || {};
  const leave = async (e) => {
    e.preventDefault();
    try { await api.post("/portal/resident/leaving-campus", { destination: f.destination, expected_return: f.expected_return ? new Date(f.expected_return).toISOString() : null }); toast.success("PACT Life Pass opened — be safe"); setF({ destination: "", expected_return: "" }); load(); }
    catch (err) { toast.error(lpErr(err)); }
  };
  const back = async () => { try { await api.post("/portal/resident/return-to-campus"); toast.success("Welcome back"); load(); } catch (err) { toast.error(lpErr(err)); } };
  return (
    <section className={`${card} space-y-3`} data-testid="home-lifepass">
      <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#d4a017]" /><h2 className={h2}>PACT Life Pass</h2>
        <span data-testid="home-lifepass-status" className={`ml-auto text-xs ${out ? "text-amber-300" : "text-emerald-300"}`}>{out ? "Off property" : "On property"}</span></div>
      {out ? (
        <>
          <p className="text-sm text-white/70">Out to <b>{co.destination}</b> since {when(co.left_at)}{co.expected_return ? ` · back by ${when(co.expected_return)}` : ""}</p>
          <button type="button" className={btn} onClick={back} data-testid="home-lifepass-return">I'm back on property</button>
        </>
      ) : (
        <form onSubmit={leave} className="grid gap-2 sm:grid-cols-[1fr_200px_auto]">
          <input className={input} required placeholder="Where are you going?" value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} data-testid="home-lifepass-destination" />
          <input type="datetime-local" className={input} value={f.expected_return} onChange={(e) => setF({ ...f, expected_return: e.target.value })} data-testid="home-lifepass-back-by" aria-label="Back by" />
          <button className={btn} data-testid="home-lifepass-leave">Leaving</button>
        </form>
      )}
    </section>
  );
}

export function RoomReadyCard({ token }) {
  const api = lpApi(token);
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/portal/resident/room-ready").then(setS).catch(() => setS(null)), [token]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (!s) return <section className={`${card} h-[68px] animate-pulse`} />;
  const tap = async () => { try { await api.post("/portal/resident/room-ready"); toast.success("Room ready sent"); load(); } catch (e) { toast.error(lpErr(e)); } };
  return (
    <section className={`${card} flex items-center gap-3`} data-testid="home-room-ready">
      <BedDouble className="h-5 w-5 text-[#d4a017] shrink-0" />
      <div className="flex-1 text-sm"><div className="font-medium">Room ready</div><div className="text-white/50 text-xs">{s.tapped ? `Sent today${s.checkin?.result ? ` · ${s.checkin.result}` : ""}` : "Tap when your room is ready for inspection"}</div></div>
      <button type="button" disabled={s.tapped} className={s.tapped ? ghost : btn} onClick={tap} data-testid="home-room-ready-tap">{s.tapped ? "Sent" : "Room ready"}</button>
    </section>
  );
}

export function ProfileCard({ token }) {
  const [me, setMe] = useState(null);
  useEffect(() => { lpApi(token).get("/portal/resident/me").then(setMe).catch(() => setMe(null)); }, [token]);
  if (!me) return null;
  return (
    <section className={`${card} space-y-1 text-sm`} data-testid="more-profile">
      <h2 className={h2}>My profile</h2>
      <div className="text-lg font-semibold">{me.preferred_name || me.first_name} {me.last_name}</div>
      <div className="text-white/60">Phase {me.phase || "—"} · {me.status}</div>
      {me.housing?.property_name && <div className="text-white/60">House: {me.housing.property_name}{me.housing.room ? ` · Room ${me.housing.room}` : ""}</div>}
      {me.intake_date && <div className="text-white/40 text-xs">Started {new Date(me.intake_date).toLocaleDateString()}</div>}
    </section>
  );
}
